import { message, Select, Switch } from 'antd';
import { Plus, RefreshCcw } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { confirm } from '@/components/common/ConfirmDialog';
import { Pagination } from '@/components/common/Pagination';
import { SearchBar } from '@/components/common/SearchBar';
import { Button } from '@/components/ui/button';
import type { AccountLoginCode } from '@/stores';
import { authStore, brandStore, loginCodeStore } from '@/stores';
import { AccountLoginCodeStatus } from '@/types';
import { CreateLoginCodeDialog } from './components/CreateLoginCodeDialog';
import { LoginCodeTable } from './components/LoginCodeTable';
import { UpdateExpiryDialog } from './components/UpdateExpiryDialog';

export const LoginCodePage = observer(() => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [expiryDialogOpen, setExpiryDialogOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<AccountLoginCode | null>(
    null,
  );
  const prevPaginationRef = useRef<{
    brandId: string;
    page: number;
    pageSize: number;
    statusFilter: AccountLoginCodeStatus | undefined;
  } | null>(null);

  const activeBrand = brandStore.activeBrand;
  const activeBrandId = brandStore.activeBrandId;
  const activeAppId = brandStore.activeBrandAppId;

  const loadPageData = async () => {
    if (!activeAppId) {
      return;
    }
    await Promise.all([
      loginCodeStore.fetchSettings(activeAppId),
      loginCodeStore.fetchLoginCodes(activeAppId),
    ]);
  };

  useEffect(() => {
    if (!activeAppId) {
      return;
    }
    void loginCodeStore.fetchSettings(activeAppId);
  }, [activeAppId]);

  const page = loginCodeStore.pagination.page;
  const pageSize = loginCodeStore.pagination.pageSize;
  const statusFilter = loginCodeStore.statusFilter;
  useEffect(() => {
    const prev = prevPaginationRef.current;
    if (
      !activeAppId ||
      !prev ||
      prev.brandId !== activeAppId ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.statusFilter !== statusFilter
    ) {
      prevPaginationRef.current = {
        brandId: activeAppId,
        page,
        pageSize,
        statusFilter,
      };
      void loginCodeStore.fetchLoginCodes(activeAppId);
    }
  }, [activeAppId, page, pageSize, statusFilter]);

  const handleSearch = (value: string) => {
    loginCodeStore.setSearchQuery(value);
    if (!activeAppId) {
      return;
    }
    void loginCodeStore.fetchLoginCodes(activeAppId);
  };

  const handleToggleSettings = async (enabled: boolean) => {
    if (!activeAppId) {
      return;
    }

    try {
      await loginCodeStore.updateSettings(
        activeAppId,
        enabled,
        authStore.user?.user_id,
      );
      message.success(enabled ? '登录码总开关已开启' : '登录码总开关已关闭');
    } catch (error) {
      console.error('更新登录码开关失败:', error);
      message.error('更新登录码开关失败，请稍后重试');
    }
  };

  const handleEditExpiry = (loginCode: AccountLoginCode) => {
    setSelectedCode(loginCode);
    setExpiryDialogOpen(true);
  };

  const handleClearExpiry = (loginCode: AccountLoginCode) => {
    if (!activeAppId) {
      return;
    }

    confirm({
      title: '确认清空过期时间',
      content: '清空后该登录码会变成长效码，仅受总开关和删除状态控制。',
      okText: '确认清空',
      cancelText: '取消',
      onOk: async () => {
        try {
          await loginCodeStore.updateLoginCodeExpiry(activeAppId, {
            id: loginCode.id,
            clear_expires_at: true,
            updated_by: authStore.user?.user_id,
          });
          message.success('过期时间已清空');
        } catch (error) {
          console.error('清空过期时间失败:', error);
          message.error('清空过期时间失败，请稍后重试');
        }
      },
    });
  };

  const handleDelete = (loginCode: AccountLoginCode) => {
    if (!activeAppId) {
      return;
    }

    confirm({
      title: '确认删除登录码',
      content: (
        <div className="space-y-2 text-sm">
          <p>
            登录码{' '}
            <span className="font-mono font-medium">
              {loginCode.codeMasked}
            </span>{' '}
            将被标记为删除，之后不能再用于登录。
          </p>
          <p className="text-muted-foreground">
            已存在的历史账号不会被删除，只是这张码失效。
          </p>
        </div>
      ),
      okText: '确认删除',
      cancelText: '取消',
      type: 'error',
      onOk: async () => {
        try {
          await loginCodeStore.deleteLoginCode(
            activeAppId,
            loginCode.id,
            authStore.user?.user_id,
          );
          message.success('登录码已删除');
        } catch (error) {
          console.error('删除登录码失败:', error);
          message.error('删除登录码失败，请稍后重试');
        }
      },
    });
  };

  const settings = loginCodeStore.settings;
  const statusOptions = useMemo(
    () => [
      { value: 'all', label: '全部状态' },
      {
        value: AccountLoginCodeStatus.ACCOUNT_LOGIN_CODE_STATUS_ACTIVE,
        label: '可用',
      },
      {
        value: AccountLoginCodeStatus.ACCOUNT_LOGIN_CODE_STATUS_DELETED,
        label: '已删除',
      },
    ],
    [],
  );

  return (
    <>
      <div className="rounded-lg border border-border bg-white shadow-sm">
        <div className="space-y-6 p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold text-foreground">
                  登录码管理
                </h1>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  {activeBrand?.name || '未选择 Brand'}
                </span>
              </div>
              <p className="max-w-3xl text-sm text-muted-foreground">
                这里管理的是独立的登录码登录能力，不属于邀请码/内测申请流程。每个码都可以长期有效、单独删除、单独设过期，并复用
                account 现有账号与 session 体系。
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => void loadPageData()}>
                <RefreshCcw className="mr-2 h-4 w-4" />
                刷新
              </Button>
              <Button
                onClick={() => setCreateDialogOpen(true)}
                disabled={!activeAppId}
              >
                <Plus className="mr-2 h-4 w-4" />
                新增登录码
              </Button>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="rounded-xl border border-border bg-slate-50 p-4">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                当前 Brand
              </p>
              <p className="mt-2 text-base font-semibold text-foreground">
                {activeBrand?.name || '-'}
              </p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                {activeBrandId || '未选择'}
              </p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                App ID: {activeAppId || '未选择'}
              </p>
            </div>

            <div className="rounded-xl border border-border bg-slate-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                    登录码总开关
                  </p>
                  <p className="mt-2 text-base font-semibold text-foreground">
                    {settings?.enabled ? '已开启' : '已关闭'}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {settings?.updatedBy
                      ? `最近更新人：${settings.updatedBy}`
                      : '未设置时默认关闭'}
                  </p>
                </div>
                <Switch
                  checked={Boolean(settings?.enabled)}
                  loading={loginCodeStore.settingsLoading}
                  disabled={!activeAppId}
                  onChange={(checked) => void handleToggleSettings(checked)}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <SearchBar
              placeholder="搜索展示名、用户ID或登录码掩码..."
              onSearch={handleSearch}
              className="max-w-xl"
              loading={loginCodeStore.loading}
            />
            <div className="flex items-center gap-3">
              <Select
                value={statusFilter ?? 'all'}
                options={statusOptions}
                className="min-w-[160px]"
                onChange={(value) => {
                  loginCodeStore.setStatusFilter(
                    value === 'all'
                      ? undefined
                      : (value as AccountLoginCodeStatus),
                  );
                }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <LoginCodeTable
              onEditExpiry={handleEditExpiry}
              onClearExpiry={handleClearExpiry}
              onDelete={handleDelete}
            />
            <Pagination
              page={loginCodeStore.pagination.page}
              pageSize={loginCodeStore.pagination.pageSize}
              total={loginCodeStore.pagination.total}
              onPageChange={(nextPage) => {
                loginCodeStore.setPagination(nextPage);
              }}
            />
          </div>
        </div>
      </div>

      <CreateLoginCodeDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
      <UpdateExpiryDialog
        open={expiryDialogOpen}
        onOpenChange={(open) => {
          setExpiryDialogOpen(open);
          if (!open) {
            setSelectedCode(null);
          }
        }}
        loginCode={selectedCode}
      />
    </>
  );
});
